import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  formatImeiHubPriceEur,
  formatImeiHubStatCount,
  IMEI_HUB_SERVICE_IDS,
  isImeiHubPaidService,
  type ImeiHubServiceId,
  type ImeiHubStatsSnapshot,
} from '@redevicer/shared'
import { imeiDigitsFromUserInput, isImeiInputAllowedChars, isLikelyImei15 } from '../utils/extractImei'
import { checkImei, imeiAPI } from '../api/client'
import ImeiHubSignalIcon from './ImeiHubSignalIcon'
import ImeiVerifyReport from './ImeiVerifyReport'
import VerifyFlow from './verify-loading/VerifyFlow'
import ImeiHubPanelDocs from './ImeiHubPanelDocs'
import ImeiHubServiceTitle, { imeiHubServiceProductLabel } from './ImeiHubServiceTitle'
import ImeiHubServicePriceBadge from './ImeiHubServicePriceBadge'
import { maskImeiDigits, type ImeiCheckPayload } from '../utils/imeiReportModel'
import {
  authTokenForImeiHub,
  formatImeiHubCheckError,
  redirectToImeiHubCheckout,
} from '../utils/imeiHubCatalogClient'
import { storePendingImeiReport } from '../utils/pendingImeiReport'
import { readImeiHubStatsCache, writeImeiHubStatsCache } from '../utils/imeiHubStatsCache'
import { notifyImeiReportsUpdated } from '../utils/imeiReportsUnread'
import { rememberImeiCheckDigits } from '../utils/imeiReportImeiCache'
import { IMEI_FOLLOW_UP_EVENT, type ImeiFollowUpCheckDetail } from '../utils/imeiFollowUpCheck'
import {
  claimImeiCheckPaidReturnRun,
  consumePendingImeiCheckPaidReturn,
  IMEI_CHECK_PAID_EVENT,
  releaseImeiCheckPaidReturnRun,
  takePaidCheckoutSession,
  type ImeiCheckPaidDetail,
} from '../utils/pendingImeiCheckPayment'

export type { ImeiHubServiceId }

/** Must match `.vc-imei-hub-picker-sheet` / root backdrop in index.css */
const SERVICE_PICKER_SHEET_MS = 320
const SERVICE_PICKER_BACKDROP_MS = 220
const SERVICE_PICKER_SHEET_EASING = 'cubic-bezier(0.32, 1.1, 0.48, 1)'
const SERVICE_PICKER_BACKDROP_EASING = 'ease'

function playServicePickerCloseAnimation(
  sheet: HTMLDivElement,
  root: HTMLDivElement | null,
  dragPx: number,
): number {
  for (const anim of sheet.getAnimations()) anim.cancel()
  if (root) for (const anim of root.getAnimations()) anim.cancel()

  sheet.style.transition = ''
  if (dragPx > 0) {
    sheet.style.transform = `translateY(${dragPx}px)`
    sheet.style.opacity = '1'
  } else {
    sheet.style.removeProperty('transform')
    sheet.style.removeProperty('opacity')
  }

  sheet.animate(
    [
      { transform: `translateY(${dragPx}px)`, opacity: 1 },
      { transform: 'translateY(100%)', opacity: 0 },
    ],
    {
      duration: SERVICE_PICKER_SHEET_MS,
      easing: SERVICE_PICKER_SHEET_EASING,
      fill: 'forwards',
    },
  )

  root?.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: SERVICE_PICKER_BACKDROP_MS,
    easing: SERVICE_PICKER_BACKDROP_EASING,
    fill: 'forwards',
  })

  if (root) root.style.pointerEvents = 'none'

  return Math.max(SERVICE_PICKER_SHEET_MS, SERVICE_PICKER_BACKDROP_MS)
}

/** Matches `.vc-imei-hub__panel` open/close transition in CSS. */
const IMEI_HUB_PANEL_TRANSITION_MS = 320

type CheckResult = ImeiCheckPayload

type Props = {
  token?: string | null
  onRequireLogin?: () => void
}

function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3 20 6v6c0 5-3.5 9.5-8 9-4.5-.5-8-4-8-9V6l8-3Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="m9.2 12.1 1.9 1.9 3.8-3.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

type RiskStatId = 'stolen' | 'locked' | 'financed'

const RISK_STATS: Array<{ id: RiskStatId; labelKey: string }> = [
  { id: 'stolen', labelKey: 'vc.imeiHub.stolenTodayLabel' },
  { id: 'locked', labelKey: 'vc.imeiHub.lockedTodayLabel' },
  { id: 'financed', labelKey: 'vc.imeiHub.financedTodayLabel' },
]

function formatChecksCount(value: number, locale: string): string {
  return formatImeiHubStatCount(value, locale)
}

function ImeiHubStatsMinimal({
  checksTotal,
  riskStats,
  locale,
  t,
}: {
  checksTotal: number
  riskStats: Record<RiskStatId, number>
  locale: string
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <p
      className="vc-imei-hub__stats-minimal"
      aria-label={t('vc.imeiHub.statsMinimalAria', {
        total: checksTotal,
        stolen: riskStats.stolen,
        locked: riskStats.locked,
        financed: riskStats.financed,
      })}
    >
      <span className="vc-imei-hub__stats-minimal-total">
        <strong className="vc-imei-hub__stats-minimal-num">{formatChecksCount(checksTotal, locale)}</strong>
        <span className="vc-imei-hub__stats-minimal-total-label">{t('vc.imeiHub.checksCountLabel')}</span>
      </span>
      <span className="vc-imei-hub__stats-minimal-of">{t('vc.imeiHub.statsMinimalOf')}</span>
      {RISK_STATS.map(({ id, labelKey }, index) => (
        <span key={id} className="vc-imei-hub__stats-minimal-segment">
          {index > 0 ? (
            <span className="vc-imei-hub__stats-minimal-sep" aria-hidden="true">
              ·
            </span>
          ) : null}
          <span
            className={`vc-imei-hub__stats-minimal-risk vc-imei-hub__stats-minimal-risk--${id}${riskStats[id] > 0 ? ' vc-imei-hub__stats-minimal-risk--active' : ''}`}
          >
            <strong className="vc-imei-hub__stats-minimal-num">{formatChecksCount(riskStats[id], locale)}</strong>{' '}
            {t(labelKey)}
          </span>
        </span>
      ))}
    </p>
  )
}

function CardChevron({ open, tile = false }: { open: boolean; tile?: boolean }) {
  return (
    <span
      className={`vc-imei-hub__card-chevron${tile ? ' vc-imei-hub__card-chevron--tile' : ''}${open ? ' vc-imei-hub__card-chevron--open' : ''}`}
      aria-hidden="true"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path
          d="M6 9l6 6 6-6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

function ImeiHubServicePanel({
  serviceId,
  priceEurCents,
}: {
  serviceId: ImeiHubServiceId
  priceEurCents?: number | null
}) {
  return (
    <div className="vc-imei-hub__panel-content vc-imei-hub__panel-content--docs" role="region">
      <ImeiHubPanelDocs service={serviceId} priceEurCents={priceEurCents} />
    </div>
  )
}

export default function ImeiHubSection({ token = null, onRequireLogin }: Props) {
  const { t, i18n } = useTranslation()
  const inputId = useId()
  const groupId = useId()
  const panelId = useId()
  const servicePickerId = useId()
  const servicePickerLabelId = useId()
  const [stackPanelInline, setStackPanelInline] = useState(false)
  const [value, setValue] = useState('')
  const [selected, setSelected] = useState<ImeiHubServiceId>('verify')
  const [expandedId, setExpandedId] = useState<ImeiHubServiceId | null>(null)
  const [servicePickerOpen, setServicePickerOpen] = useState(false)
  const [servicePickerClosing, setServicePickerClosing] = useState(false)
  const servicePickerRootRef = useRef<HTMLDivElement>(null)
  const servicePickerSheetRef = useRef<HTMLDivElement>(null)
  const servicePickerListRef = useRef<HTMLUListElement>(null)
  const servicePickerCloseTimerRef = useRef<number | null>(null)
  const hubCardsWrapRef = useRef<HTMLDivElement>(null)
  const hubCardsMarqueeRef = useRef<HTMLDivElement>(null)
  const hubCardsMarqueeTrackRef = useRef<HTMLDivElement>(null)
  const [hubCardsCarousel, setHubCardsCarousel] = useState(false)
  const hubCardsMarqueeOffsetRef = useRef(0)
  /** 1 = toward last card, -1 = back toward first */
  const hubCardsMarqueeDirectionRef = useRef(1)
  const hubCardsMarqueePauseRef = useRef(false)
  const hubCardsMarqueeRafRef = useRef<number>(0)
  const hubCardsMarqueeResumeTimerRef = useRef<number | null>(null)
  const hubCardsMarqueeDragRef = useRef({
    pointerId: -1,
    startX: 0,
    startOffset: 0,
    dragging: false,
  })
  /** Blocks one stray click right after a drag (without eating the next real tap). */
  const hubCardsMarqueeDragEndedRef = useRef(false)
  /** Keeps closed panel DOM mounted through the height transition (avoids scroll jump on mobile). */
  const [panelMountIds, setPanelMountIds] = useState<Set<ImeiHubServiceId>>(() => new Set())
  const panelUnmountTimersRef = useRef<Map<ImeiHubServiceId, number>>(new Map())
  const [loading, setLoading] = useState(false)
  const [verifySessionKey, setVerifySessionKey] = useState(0)
  const [verifySucceeded, setVerifySucceeded] = useState(false)
  const [verifyFailed, setVerifyFailed] = useState(false)
  const [verifyFlowError, setVerifyFlowError] = useState<string | null>(null)
  const [revealReport, setRevealReport] = useState(false)
  const [flowImeiMasked, setFlowImeiMasked] = useState('')
  const [validationError, setValidationError] = useState('')
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null)
  const checkResultRef = useRef<CheckResult | null>(null)
  const paidReturnTimerRef = useRef<number | null>(null)
  const paidReturnClaimRef = useRef<string | null>(null)
  const [checkedImeiMasked, setCheckedImeiMasked] = useState('')
  const [checkedAt, setCheckedAt] = useState<Date | null>(null)
  const [integrationNote, setIntegrationNote] = useState('')
  const [paidPriceCents, setPaidPriceCents] = useState<Partial<Record<ImeiHubServiceId, number>>>({})
  const [hubCatalogReady, setHubCatalogReady] = useState(false)
  const [stripeCheckoutEnabled, setStripeCheckoutEnabled] = useState(false)
  const [checksTotal, setChecksTotal] = useState(0)
  const [riskStats, setRiskStats] = useState<Record<RiskStatId, number>>({
    stolen: 0,
    locked: 0,
    financed: 0,
  })
  const [checksStatsReady, setChecksStatsReady] = useState(false)

  const closeServicePicker = useCallback((opts?: { sheetDragPx?: number }) => {
    if (!servicePickerOpen || servicePickerClosing) return

    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setServicePickerClosing(true)

    const sheet = servicePickerSheetRef.current
    const root = servicePickerRootRef.current
    const dragPx = opts?.sheetDragPx ?? 0

    let closeMs = 0
    if (!reducedMotion && sheet) {
      closeMs = playServicePickerCloseAnimation(sheet, root, dragPx)
    }

    if (closeMs === 0) {
      setServicePickerClosing(false)
      setServicePickerOpen(false)
      if (sheet) {
        for (const anim of sheet.getAnimations()) anim.cancel()
        sheet.style.transition = ''
        sheet.style.transform = ''
        sheet.style.opacity = ''
      }
      if (root) {
        for (const anim of root.getAnimations()) anim.cancel()
        root.style.transition = ''
        root.style.opacity = ''
        root.style.pointerEvents = ''
      }
      return
    }

    if (servicePickerCloseTimerRef.current != null) {
      window.clearTimeout(servicePickerCloseTimerRef.current)
    }
    servicePickerCloseTimerRef.current = window.setTimeout(() => {
      servicePickerCloseTimerRef.current = null
      setServicePickerClosing(false)
      setServicePickerOpen(false)
      if (sheet) {
        for (const anim of sheet.getAnimations()) anim.cancel()
        sheet.style.transition = ''
        sheet.style.transform = ''
        sheet.style.opacity = ''
      }
      if (root) {
        for (const anim of root.getAnimations()) anim.cancel()
        root.style.transition = ''
        root.style.opacity = ''
        root.style.pointerEvents = ''
      }
    }, closeMs + 24)
  }, [servicePickerOpen, servicePickerClosing])

  useEffect(() => {
    return () => {
      if (servicePickerCloseTimerRef.current != null) {
        window.clearTimeout(servicePickerCloseTimerRef.current)
      }
    }
  }, [])

  useLayoutEffect(() => {
    if (!servicePickerOpen || !stackPanelInline) return

    const scrollY = window.scrollY
    const html = document.documentElement
    const body = document.body
    html.classList.add('listing-detail-scroll-lock')
    body.style.top = `-${scrollY}px`

    const sheet = servicePickerSheetRef.current
    const drag = { startY: 0, dy: 0, active: false }

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeServicePicker()
    }

    function onTouchStart(e: TouchEvent) {
      if (servicePickerCloseTimerRef.current != null) return
      if (e.touches.length !== 1) return
      drag.startY = e.touches[0].clientY
      drag.dy = 0
      drag.active = true
    }

    function onTouchMove(e: TouchEvent) {
      if (!drag.active || e.touches.length !== 1) return
      const sheetEl = servicePickerSheetRef.current
      if (!sheetEl) return

      const y = e.touches[0].clientY
      const dy = y - drag.startY
      const target = e.target
      const list = servicePickerListRef.current
      const onList = target instanceof Element && list != null && list.contains(target)

      if (onList && list) {
        if (dy < 0 && list.scrollTop + list.clientHeight < list.scrollHeight - 1) return
        if (dy > 0 && list.scrollTop > 0) return
      }

      if (dy > 0) {
        e.preventDefault()
        drag.dy = dy
        sheetEl.style.transform = `translateY(${Math.min(dy, 140)}px)`
        sheetEl.style.transition = 'none'
      } else if (drag.dy > 0) {
        sheetEl.style.transform = ''
        drag.dy = 0
      }
    }

    function onTouchEnd() {
      const sheetEl = servicePickerSheetRef.current
      if (!sheetEl) return
      sheetEl.style.transition = ''
      if (drag.dy > 72) {
        closeServicePicker({ sheetDragPx: Math.min(drag.dy, 140) })
      } else {
        sheetEl.style.transform = ''
      }
      drag.active = false
      drag.dy = 0
    }

    function preventBackgroundTouchMove(e: TouchEvent) {
      const target = e.target
      if (!(target instanceof Element)) {
        e.preventDefault()
        return
      }
      if (target.closest('.vc-imei-hub-picker-sheet')) return
      e.preventDefault()
    }

    function preventBackgroundWheel(e: WheelEvent) {
      e.preventDefault()
    }

    function preventBackgroundScroll() {
      if (window.scrollY !== scrollY) window.scrollTo(0, scrollY)
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('touchmove', preventBackgroundTouchMove, { passive: false })
    window.addEventListener('wheel', preventBackgroundWheel, { passive: false })
    window.addEventListener('scroll', preventBackgroundScroll, { passive: true })
    sheet?.addEventListener('touchstart', onTouchStart, { passive: true })
    sheet?.addEventListener('touchmove', onTouchMove, { passive: false })
    sheet?.addEventListener('touchend', onTouchEnd)
    sheet?.addEventListener('touchcancel', onTouchEnd)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('touchmove', preventBackgroundTouchMove)
      window.removeEventListener('wheel', preventBackgroundWheel)
      window.removeEventListener('scroll', preventBackgroundScroll)
      sheet?.removeEventListener('touchstart', onTouchStart)
      sheet?.removeEventListener('touchmove', onTouchMove)
      sheet?.removeEventListener('touchend', onTouchEnd)
      sheet?.removeEventListener('touchcancel', onTouchEnd)
      html.classList.remove('listing-detail-scroll-lock')
      body.style.top = ''
      window.scrollTo(0, scrollY)
      if (sheet) {
        sheet.style.transform = ''
        sheet.style.transition = ''
      }
    }
  }, [servicePickerOpen, stackPanelInline, closeServicePicker])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 960px)')
    const sync = () => setStackPanelInline(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  const hubCardsTileMode = stackPanelInline || hubCardsCarousel

  useEffect(() => {
    const timers = panelUnmountTimersRef.current
    return () => {
      for (const timer of timers.values()) window.clearTimeout(timer)
      timers.clear()
    }
  }, [])

  function mountPanel(id: ImeiHubServiceId) {
    const pending = panelUnmountTimersRef.current.get(id)
    if (pending != null) {
      window.clearTimeout(pending)
      panelUnmountTimersRef.current.delete(id)
    }
    setPanelMountIds((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  function schedulePanelUnmount(id: ImeiHubServiceId) {
    const pending = panelUnmountTimersRef.current.get(id)
    if (pending != null) window.clearTimeout(pending)
    const timer = window.setTimeout(() => {
      panelUnmountTimersRef.current.delete(id)
      setPanelMountIds((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }, IMEI_HUB_PANEL_TRANSITION_MS)
    panelUnmountTimersRef.current.set(id, timer)
  }

  function applyStats(data: Partial<ImeiHubStatsSnapshot>) {
    if (typeof data.total_checks === 'number') setChecksTotal(data.total_checks)
    setRiskStats((prev) => ({
      stolen: typeof data.stolen_today === 'number' ? data.stolen_today : prev.stolen,
      locked: typeof data.locked_today === 'number' ? data.locked_today : prev.locked,
      financed: typeof data.financed_today === 'number' ? data.financed_today : prev.financed,
    }))
    if (
      typeof data.total_checks === 'number' &&
      typeof data.stolen_today === 'number' &&
      typeof data.locked_today === 'number' &&
      typeof data.financed_today === 'number'
    ) {
      writeImeiHubStatsCache({
        total_checks: data.total_checks,
        stolen_today: data.stolen_today,
        locked_today: data.locked_today,
        financed_today: data.financed_today,
      })
    }
  }

  function refreshStats() {
    return imeiAPI.getStats().then((res) => {
      applyStats(res.data as Partial<ImeiHubStatsSnapshot>)
    })
  }

  useEffect(() => {
    void imeiAPI
      .getCatalog()
      .then((res) => {
        const data = res.data as {
          stripe_enabled?: boolean
          services?: { id: string; paid?: boolean; price_eur_cents?: number | null }[]
        }
        setStripeCheckoutEnabled(Boolean(data.stripe_enabled))
        const next: Partial<Record<ImeiHubServiceId, number>> = {}
        for (const row of data.services ?? []) {
          if (row.paid && typeof row.price_eur_cents === 'number') {
            next[row.id as ImeiHubServiceId] = row.price_eur_cents
          }
        }
        setPaidPriceCents(next)
      })
      .catch(() => {})
      .finally(() => setHubCatalogReady(true))
  }, [])

  const orderedServiceIds = useMemo(() => {
    const isPaid = (id: ImeiHubServiceId) => {
      if (hubCatalogReady) return paidPriceCents[id] != null
      return isImeiHubPaidService(id)
    }
    return [...IMEI_HUB_SERVICE_IDS].sort((a, b) => {
      const paidOrder = Number(isPaid(a)) - Number(isPaid(b))
      if (paidOrder !== 0) return paidOrder
      return IMEI_HUB_SERVICE_IDS.indexOf(a) - IMEI_HUB_SERVICE_IDS.indexOf(b)
    })
  }, [hubCatalogReady, paidPriceCents])

  /** Mobile tiles carousel below 960px; desktop grid from 1030px container width up. */
  useLayoutEffect(() => {
    if (stackPanelInline) {
      setHubCardsCarousel(true)
      return
    }
    const wrap = hubCardsWrapRef.current
    if (!wrap || typeof ResizeObserver === 'undefined') return

    const DESKTOP_GRID_MIN_PX = 1030
    const sync = () => setHubCardsCarousel(wrap.clientWidth < DESKTOP_GRID_MIN_PX)
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(wrap)
    return () => ro.disconnect()
  }, [stackPanelInline])

  /** Hub cards carousel: auto-scroll to end, then reverse to start (not infinite loop). */
  useEffect(() => {
    if (!hubCardsTileMode) return
    const el = hubCardsMarqueeRef.current
    const track = hubCardsMarqueeTrackRef.current
    if (!el || !track) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const travelSeconds = 72
    const dragThresholdPx = 10

    hubCardsMarqueeOffsetRef.current = 0
    hubCardsMarqueeDirectionRef.current = 1

    const maxScroll = () => Math.max(0, track.scrollWidth - el.clientWidth)

    const clampOffset = (value: number) => {
      const max = maxScroll()
      return Math.min(max, Math.max(0, value))
    }

    const applyOffset = () => {
      track.style.transform = `translate3d(${-hubCardsMarqueeOffsetRef.current}px, 0, 0)`
    }

    const pauseAuto = () => {
      hubCardsMarqueePauseRef.current = true
      if (hubCardsMarqueeResumeTimerRef.current != null) {
        window.clearTimeout(hubCardsMarqueeResumeTimerRef.current)
        hubCardsMarqueeResumeTimerRef.current = null
      }
    }

    const scheduleResumeAuto = () => {
      if (hubCardsMarqueeResumeTimerRef.current != null) {
        window.clearTimeout(hubCardsMarqueeResumeTimerRef.current)
      }
      hubCardsMarqueeResumeTimerRef.current = window.setTimeout(() => {
        hubCardsMarqueeResumeTimerRef.current = null
        hubCardsMarqueePauseRef.current = false
      }, 1200)
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.button > 0) return
      pauseAuto()
      hubCardsMarqueeDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startOffset: hubCardsMarqueeOffsetRef.current,
        dragging: false,
      }
    }

    const onPointerMove = (e: PointerEvent) => {
      const drag = hubCardsMarqueeDragRef.current
      if (drag.pointerId < 0 || e.pointerId !== drag.pointerId) return
      const dx = e.clientX - drag.startX
      if (!drag.dragging) {
        if (Math.abs(dx) <= dragThresholdPx) return
        drag.dragging = true
        pauseAuto()
        el.classList.add('vc-imei-hub__cards-marquee--dragging')
        el.setPointerCapture(e.pointerId)
      }
      e.preventDefault()
      hubCardsMarqueeOffsetRef.current = clampOffset(drag.startOffset - dx)
      applyOffset()
    }

    const onPointerUp = (e: PointerEvent) => {
      const drag = hubCardsMarqueeDragRef.current
      if (drag.pointerId < 0 || e.pointerId !== drag.pointerId) return
      if (drag.dragging) {
        e.preventDefault()
        hubCardsMarqueeDragEndedRef.current = true
        if (el.hasPointerCapture(e.pointerId)) {
          el.releasePointerCapture(e.pointerId)
        }
        el.classList.remove('vc-imei-hub__cards-marquee--dragging')
      }
      scheduleResumeAuto()
      hubCardsMarqueeDragRef.current = {
        pointerId: -1,
        startX: 0,
        startOffset: hubCardsMarqueeOffsetRef.current,
        dragging: false,
      }
    }

    el.addEventListener('pointerdown', onPointerDown, { passive: true })
    el.addEventListener('pointermove', onPointerMove, { passive: false })
    el.addEventListener('pointerup', onPointerUp, { passive: false })
    el.addEventListener('pointercancel', onPointerUp, { passive: false })

    let lastTs = performance.now()
    applyOffset()

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            hubCardsMarqueeOffsetRef.current = clampOffset(hubCardsMarqueeOffsetRef.current)
            applyOffset()
          })
        : null
    ro?.observe(el)
    ro?.observe(track)

    const tick = (now: number) => {
      const dt = Math.min(0.032, (now - lastTs) / 1000)
      lastTs = now

      const dragging = hubCardsMarqueeDragRef.current.dragging
      if (!reducedMotion && !hubCardsMarqueePauseRef.current && !dragging) {
        const max = maxScroll()
        if (max > 0) {
          const pxPerSec = max / travelSeconds
          let next =
            hubCardsMarqueeOffsetRef.current + hubCardsMarqueeDirectionRef.current * pxPerSec * dt
          if (next >= max) {
            next = max
            hubCardsMarqueeDirectionRef.current = -1
          } else if (next <= 0) {
            next = 0
            hubCardsMarqueeDirectionRef.current = 1
          }
          hubCardsMarqueeOffsetRef.current = next
          applyOffset()
        }
      }

      hubCardsMarqueeRafRef.current = requestAnimationFrame(tick)
    }

    hubCardsMarqueeRafRef.current = requestAnimationFrame(tick)

    return () => {
      ro?.disconnect()
      cancelAnimationFrame(hubCardsMarqueeRafRef.current)
      if (hubCardsMarqueeResumeTimerRef.current != null) {
        window.clearTimeout(hubCardsMarqueeResumeTimerRef.current)
      }
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('pointercancel', onPointerUp)
      el.classList.remove('vc-imei-hub__cards-marquee--dragging')
      track.style.transform = ''
      hubCardsMarqueeOffsetRef.current = 0
      hubCardsMarqueeDirectionRef.current = 1
      hubCardsMarqueePauseRef.current = false
      hubCardsMarqueeDragRef.current = {
        pointerId: -1,
        startX: 0,
        startOffset: 0,
        dragging: false,
      }
    }
  }, [hubCardsTileMode, orderedServiceIds.length, hubCatalogReady])

  useEffect(() => {
    const cached = readImeiHubStatsCache()
    if (cached) applyStats(cached)

    let cancelled = false
    void refreshStats()
      .catch(() => {
        /* keep cached / last good values — never flash zero on transient errors */
      })
      .finally(() => {
        if (!cancelled) setChecksStatsReady(true)
      })

    function onVisible() {
      if (document.visibilityState === 'visible') void refreshStats().catch(() => {})
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const trimmed = value.trim()
  const digitsOnly = imeiDigitsFromUserInput(trimmed)
  const imei15 = digitsOnly && digitsOnly.length === 15 ? digitsOnly : null
  const looksLikeImei = isImeiInputAllowedChars(trimmed)

  function clearOutput() {
    checkResultRef.current = null
    setCheckResult(null)
    setCheckedImeiMasked('')
    setCheckedAt(null)
    setIntegrationNote('')
    setValidationError('')
    setVerifySucceeded(false)
    setVerifyFailed(false)
    setVerifyFlowError(null)
    setRevealReport(false)
    setFlowImeiMasked('')
  }

  const handleVerifyReveal = useCallback(() => {
    setRevealReport(true)
    setLoading(false)
    setVerifySucceeded(false)
    setVerifyFailed(false)
  }, [])

  const verifyLoadingModalOpen = loading || verifySucceeded || verifyFailed
  const verifyReportVisible = revealReport && Boolean(checkResult)
  const verifyFlowVisible = verifyLoadingModalOpen || verifyReportVisible
  const verifyFlowLoadingActive = loading || verifySucceeded || verifyFailed
  const verifyLoadingDismissible = verifyFailed

  function dismissVerifyLoadingModal() {
    if (verifyFailed) {
      setVerifyFailed(false)
      setVerifyFlowError(null)
      setValidationError('')
    }
  }
  const checkSubmitBusy = loading || verifySucceeded

  function selectService(id: ImeiHubServiceId) {
    setSelected(id)
    clearOutput()
  }

  function pickServiceFromList(id: ImeiHubServiceId) {
    selectService(id)
    closeServicePicker()
    if (hubCardsTileMode) resumeHubCardsMarqueeNow()
  }

  function toggleDocsPanel(id: ImeiHubServiceId) {
    const prevExpanded = expandedId
    const nextExpanded = prevExpanded === id ? null : id

    if (prevExpanded && prevExpanded !== nextExpanded) {
      schedulePanelUnmount(prevExpanded)
    }
    if (nextExpanded) {
      mountPanel(nextExpanded)
    }

    setExpandedId(nextExpanded)
  }

  function toggleService(id: ImeiHubServiceId) {
    if (hubCardsTileMode) {
      selectService(id)
      toggleDocsPanel(id)
      resumeHubCardsMarqueeNow()
      return
    }

    const prevExpanded = expandedId
    const nextExpanded = prevExpanded === id ? null : id

    if (prevExpanded && prevExpanded !== nextExpanded) {
      schedulePanelUnmount(prevExpanded)
    }
    if (nextExpanded) {
      mountPanel(nextExpanded)
    }

    setSelected(id)
    setExpandedId(nextExpanded)
    clearOutput()
  }

  function hubServicePaid(hubService: ImeiHubServiceId): boolean {
    return paidPriceCents[hubService] != null
  }

  function ctaLabelForService(hubService: ImeiHubServiceId): string {
    const product = imeiHubServiceProductLabel(hubService, t(`vc.imeiHub.services.${hubService}.title`))
    const cents = paidPriceCents[hubService]
    if (hubServicePaid(hubService) && cents != null && stripeCheckoutEnabled) {
      return t('vc.imeiHub.ctaPay', {
        service: product,
        price: formatImeiHubPriceEur(cents, i18n.language),
      })
    }
    return t('vc.imeiHub.ctaRun', { service: product })
  }

  function executeCheck(
    digits: string,
    hubService: ImeiHubServiceId,
    checkoutSessionId?: string | null,
  ) {
    const paidSession = checkoutSessionId?.trim() || null
    setVerifySessionKey((k) => k + 1)
    setVerifySucceeded(false)
    setVerifyFailed(false)
    setVerifyFlowError(null)
    setRevealReport(false)
    setLoading(true)
    const masked = maskImeiDigits(digits)
    setFlowImeiMasked(masked)
    const authToken = authTokenForImeiHub(token)
    checkImei(digits, authToken, hubService, checkoutSessionId)
      .then((res) => {
        const result = res.data as CheckResult
        checkResultRef.current = result
        setCheckResult(result)
        setCheckedImeiMasked(masked)
        rememberImeiCheckDigits(digits, masked)
        const checkedAt = new Date()
        setCheckedAt(checkedAt)
        if (!token) {
          storePendingImeiReport({
            imei: digits,
            service: hubService,
            checkedAt: checkedAt.toISOString(),
            result,
          })
        } else {
          notifyImeiReportsUpdated()
        }
        void refreshStats().catch(() => {})
        setVerifySucceeded(true)
      })
      .catch((err) => {
        const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        if (detail === 'already_consumed' && checkResultRef.current) {
          setVerifyFailed(false)
          setVerifyFlowError(null)
          setValidationError('')
          setVerifySucceeded(true)
          return
        }
        let message = formatImeiHubCheckError(err, t)
        if (detail === 'invalid_payment_session' || detail === 'purchase_not_found') {
          message = t('vc.imeiHub.result.invalidPaymentSession')
        } else if (detail === 'already_consumed') {
          message = t('vc.imeiHub.result.alreadyConsumed')
        } else if (detail === 'stripe_session_unavailable') {
          message = t('vc.imeiHub.result.stripeSyncFailed')
        }
        setValidationError(message)
        setVerifyFlowError(message)
        setVerifyFailed(true)
        setLoading(false)
      })
      .finally(() => {
        if (paidSession) releaseImeiCheckPaidReturnRun(paidSession)
      })
  }

  function runCheck(overrides?: {
    service?: ImeiHubServiceId
    imei?: string
    checkoutSessionId?: string | null
  }) {
    setValidationError('')
    setCheckResult(null)
    setIntegrationNote('')

    const hubService = overrides?.service ?? selected
    const rawInput = (overrides?.imei ?? trimmed).trim()
    const digits = imeiDigitsFromUserInput(rawInput)
    const inputLooksLikeImei = isImeiInputAllowedChars(rawInput)

    if (!rawInput) {
      setValidationError(t('vc.imeiHub.validation.empty'))
      return
    }

    if (!inputLooksLikeImei) {
      setValidationError(t('vc.imeiHub.validation.serialUseOther'))
      return
    }
    if (!digits || digits.length !== 15 || !isLikelyImei15(digits)) {
      setValidationError(t('vc.imeiHub.validation.imeiNeed15'))
      return
    }
    if (overrides?.imei) setValue(rawInput)
    if (overrides?.service) {
      setSelected(overrides.service)
      setExpandedId(overrides.service)
    }

    if (overrides?.checkoutSessionId) {
      executeCheck(digits, hubService, overrides.checkoutSessionId)
      return
    }

    if (hubServicePaid(hubService) && stripeCheckoutEnabled) {
      const authToken = authTokenForImeiHub(token)
      const heldSession = takePaidCheckoutSession(hubService)
      if (heldSession) {
        executeCheck(digits, hubService, heldSession)
        return
      }
      setLoading(true)
      void redirectToImeiHubCheckout(digits, hubService, authToken)
        .catch((err) => {
          setValidationError(formatImeiHubCheckError(err, t))
        })
        .finally(() => setLoading(false))
      return
    }

    if (hubServicePaid(hubService) && !stripeCheckoutEnabled) {
      setValidationError(t('vc.imeiHub.result.stripeOff'))
      return
    }

    executeCheck(digits, hubService)
  }

  const runCheckRef = useRef(runCheck)
  runCheckRef.current = runCheck

  const applyPaidReturnUi = useCallback((detail: ImeiCheckPaidDetail) => {
    setSelected(detail.service as ImeiHubServiceId)
    setExpandedId(detail.service as ImeiHubServiceId)
    setValue(detail.imei)
  }, [])

  const schedulePaidCheckoutReturn = useCallback((detail: ImeiCheckPaidDetail) => {
    if (!detail.imei || !detail.service || !detail.checkoutSessionId) return
    if (!claimImeiCheckPaidReturnRun(detail.checkoutSessionId)) return
    applyPaidReturnUi(detail)
    if (paidReturnTimerRef.current != null) {
      window.clearTimeout(paidReturnTimerRef.current)
      paidReturnTimerRef.current = null
    }
    paidReturnClaimRef.current = detail.checkoutSessionId
    paidReturnTimerRef.current = window.setTimeout(() => {
      paidReturnTimerRef.current = null
      paidReturnClaimRef.current = null
      runCheckRef.current({
        service: detail.service as ImeiHubServiceId,
        imei: detail.imei,
        checkoutSessionId: detail.checkoutSessionId,
      })
    }, 120)
  }, [applyPaidReturnUi])

  useEffect(() => {
    return () => {
      if (paidReturnTimerRef.current != null) {
        window.clearTimeout(paidReturnTimerRef.current)
        paidReturnTimerRef.current = null
      }
      const claimed = paidReturnClaimRef.current
      if (claimed) {
        releaseImeiCheckPaidReturnRun(claimed)
        paidReturnClaimRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    function onFollowUp(event: Event) {
      const detail = (event as CustomEvent<ImeiFollowUpCheckDetail>).detail
      if (!detail?.service) return
      setSelected(detail.service)
      if (detail.imei) setValue(detail.imei)
      window.setTimeout(() => {
        if (detail.imei) {
          runCheckRef.current({ service: detail.service, imei: detail.imei })
        }
      }, 0)
    }
    function onPaidReturn(event: Event) {
      const detail = (event as CustomEvent<ImeiCheckPaidDetail>).detail
      if (!detail) return
      schedulePaidCheckoutReturn(detail)
    }
    window.addEventListener(IMEI_FOLLOW_UP_EVENT, onFollowUp)
    window.addEventListener(IMEI_CHECK_PAID_EVENT, onPaidReturn)
    return () => {
      window.removeEventListener(IMEI_FOLLOW_UP_EVENT, onFollowUp)
      window.removeEventListener(IMEI_CHECK_PAID_EVENT, onPaidReturn)
    }
  }, [schedulePaidCheckoutReturn])

  useEffect(() => {
    const pending = consumePendingImeiCheckPaidReturn()
    if (!pending) return
    schedulePaidCheckoutReturn(pending)
  }, [schedulePaidCheckoutReturn])

  function resumeHubCardsMarqueeNow() {
    if (hubCardsMarqueeResumeTimerRef.current != null) {
      window.clearTimeout(hubCardsMarqueeResumeTimerRef.current)
      hubCardsMarqueeResumeTimerRef.current = null
    }
    hubCardsMarqueePauseRef.current = false
  }

  function renderHubCard(id: ImeiHubServiceId, keySuffix = 0) {
    const paid = paidPriceCents[id] != null
    const active = !hubCardsTileMode && selected === id
    const open = expandedId === id
    const selectedTile = hubCardsTileMode && selected === id
    const docsActive = hubCardsTileMode && open
    return (
      <div
        key={`${id}-${keySuffix}`}
        className={`vc-imei-hub__card-group${open && !hubCardsTileMode ? ' vc-imei-hub__card-group--open' : ''}${paid ? ' vc-imei-hub__card-group--premium' : ''}`}
      >
        <button
          type="button"
          role={hubCardsTileMode ? undefined : 'radio'}
          aria-checked={hubCardsTileMode ? undefined : active}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={t(`vc.imeiHub.services.${id}.title`)}
          tabIndex={keySuffix > 0 ? -1 : undefined}
          className={`vc-imei-hub__card${paid ? ' vc-imei-hub__card--premium' : ''}${active ? ' vc-imei-hub__card--active' : ''}${selectedTile && !docsActive ? ' vc-imei-hub__card--selected' : ''}${docsActive ? ' vc-imei-hub__card--tile-active' : ''}${hubCardsTileMode ? ' vc-imei-hub__card--tile' : ''}`}
          onPointerDown={() => {
            hubCardsMarqueeDragEndedRef.current = false
          }}
          onClick={() => {
            if (hubCardsMarqueeDragEndedRef.current) {
              hubCardsMarqueeDragEndedRef.current = false
              return
            }
            toggleService(id)
          }}
        >
          <ImeiHubSignalIcon service={id} />
          <span className="vc-imei-hub__card-content">
            <span className="vc-imei-hub__card-name">
              <ImeiHubServiceTitle
                service={id}
                title={t(`vc.imeiHub.services.${id}.title`)}
                variant={hubCardsTileMode ? 'tile' : 'card'}
              />
            </span>
            <span className="vc-imei-hub__card-price">
              <ImeiHubServicePriceBadge service={id} priceEurCents={paidPriceCents[id]} variant="card" />
            </span>
          </span>
          {hubCardsTileMode ? <CardChevron open={open} tile /> : <CardChevron open={open} />}
        </button>
      </div>
    )
  }

  return (
    <section id="vc-imei-hub" className="vc-imei-hub" aria-labelledby={`${groupId}-title`}>
      <div className="vc-imei-hub__shell">
        <div className="vc-imei-hub__hero">
          <div className="vc-imei-hub__main">
            <div className="vc-imei-hub__head">
              <div className="vc-imei-hub__head-copy">
                <span className="vc-imei-hub__label">{t('vc.imeiHub.label')}</span>
                <h2 id={`${groupId}-title`} className="vc-imei-hub__title">
                  {t('vc.imeiHub.title')}
                </h2>
                <p className="vc-imei-hub__lead">{t('vc.imeiHub.lead')}</p>
              </div>
            </div>

            <form
              className={`vc-imei-hub__form${stackPanelInline ? ' vc-imei-hub__form--mobile' : ''}`}
              onSubmit={(e) => {
                e.preventDefault()
                runCheck()
              }}
            >
              <label htmlFor={inputId} className="sr-only">
                {t('vc.imeiHub.inputLabel')}
              </label>
              {stackPanelInline ? (
                <div className="vc-imei-hub__form-stack">
                  <div className="vc-imei-hub__input-wrap vc-imei-hub__input-wrap--imei-only">
                    <input
                      id={inputId}
                      className="vc-imei-hub__input"
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder={t('vc.imeiHub.inputPlaceholder')}
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value)
                        setValidationError('')
                        setCheckResult(null)
                        setIntegrationNote('')
                      }}
                      aria-describedby={
                        validationError ? `${inputId}-error` : integrationNote ? `${inputId}-note` : undefined
                      }
                    />
                  </div>

                  <div className="vc-imei-hub__service-picker">
                    <span id={servicePickerLabelId} className="vc-imei-hub__service-picker-field-label">
                      {t('vc.imeiHub.pickServiceHint')}
                    </span>
                    <button
                      type="button"
                      className={`vc-imei-hub__service-picker-trigger${servicePickerOpen ? ' vc-imei-hub__service-picker-trigger--open' : ''}`}
                      aria-expanded={servicePickerOpen}
                      aria-haspopup="listbox"
                      aria-controls={servicePickerOpen ? servicePickerId : undefined}
                      aria-labelledby={servicePickerLabelId}
                      onClick={() => {
                        if (servicePickerClosing) return
                        if (servicePickerOpen) closeServicePicker()
                        else setServicePickerOpen(true)
                      }}
                    >
                      <span className="vc-imei-hub__service-picker-trigger-row">
                        <span className="vc-imei-hub__service-picker-trigger-icon" aria-hidden>
                          <ImeiHubSignalIcon service={selected} />
                        </span>
                        <span className="vc-imei-hub__service-picker-trigger-value">
                          {imeiHubServiceProductLabel(selected, t(`vc.imeiHub.services.${selected}.title`))}
                        </span>
                      </span>
                      <CardChevron open={servicePickerOpen} />
                    </button>
                    {servicePickerOpen && typeof document !== 'undefined'
                      ? createPortal(
                          <div
                            ref={servicePickerRootRef}
                            className={`vc-imei-hub-picker-sheet-root${servicePickerClosing ? ' vc-imei-hub-picker-sheet-root--closing' : ''}`}
                            role="presentation"
                            onClick={() => closeServicePicker()}
                          >
                            <div
                              ref={servicePickerSheetRef}
                              className={`vc-imei-hub-picker-sheet${servicePickerClosing ? ' vc-imei-hub-picker-sheet--closing' : ''}`}
                              role="dialog"
                              aria-modal="true"
                              aria-labelledby={`${servicePickerId}-title`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="vc-imei-hub-picker-sheet__grab" aria-hidden />
                              <h3 id={`${servicePickerId}-title`} className="vc-imei-hub-picker-sheet__title">
                                {t('vc.imeiHub.pickService')}
                              </h3>
                              <ul
                                ref={servicePickerListRef}
                                id={servicePickerId}
                                className="vc-imei-hub-picker-sheet__list"
                                role="listbox"
                                aria-label={t('vc.imeiHub.servicePickerAria')}
                              >
                                {orderedServiceIds.map((id) => {
                                  const active = selected === id
                                  const label = imeiHubServiceProductLabel(
                                    id,
                                    t(`vc.imeiHub.services.${id}.title`),
                                  )
                                  return (
                                    <li key={id} role="presentation">
                                      <button
                                        type="button"
                                        role="option"
                                        aria-selected={active}
                                        className={`vc-imei-hub-picker-sheet__option${active ? ' vc-imei-hub-picker-sheet__option--active' : ''}`}
                                        onClick={() => pickServiceFromList(id)}
                                      >
                                        <span className="vc-imei-hub-picker-sheet__option-icon" aria-hidden>
                                          <ImeiHubSignalIcon service={id} />
                                        </span>
                                        <span className="vc-imei-hub-picker-sheet__option-text">
                                          <span className="vc-imei-hub-picker-sheet__option-label">{label}</span>
                                          <ImeiHubServicePriceBadge
                                            service={id}
                                            priceEurCents={paidPriceCents[id]}
                                            variant="picker"
                                          />
                                        </span>
                                        {active ? (
                                          <span className="vc-imei-hub-picker-sheet__option-check" aria-hidden>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                              <path
                                                d="m5 12.5 4.2 4.2L19 7"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </span>
                                        ) : null}
                                      </button>
                                    </li>
                                  )
                                })}
                              </ul>
                            </div>
                          </div>,
                          document.body,
                        )
                      : null}
                  </div>

                  <button
                    type="submit"
                    className="vc-imei-hub__cta vc-imei-hub__cta--run"
                    disabled={checkSubmitBusy}
                  >
                    {checkSubmitBusy ? '…' : ctaLabelForService(selected)}
                  </button>
                </div>
              ) : (
                <div className="vc-imei-hub__input-wrap">
                  <input
                    id={inputId}
                    className="vc-imei-hub__input"
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={t('vc.imeiHub.inputPlaceholder')}
                    value={value}
                    onChange={(e) => {
                      setValue(e.target.value)
                      setValidationError('')
                      setCheckResult(null)
                      setIntegrationNote('')
                    }}
                    aria-describedby={
                      validationError ? `${inputId}-error` : integrationNote ? `${inputId}-note` : undefined
                    }
                  />
                  <button
                    type="submit"
                    className="vc-imei-hub__cta vc-imei-hub__cta--run"
                    disabled={checkSubmitBusy}
                    aria-label={t('vc.imeiHub.ctaRun', {
                      service: imeiHubServiceProductLabel(selected, t(`vc.imeiHub.services.${selected}.title`)),
                    })}
                  >
                    {checkSubmitBusy ? '…' : ctaLabelForService(selected)}
                  </button>
                </div>
              )}
            </form>

            {validationError ? (
              <p id={`${inputId}-error`} className="vc-imei-hub__error" role="alert">
                {validationError}
              </p>
            ) : null}
          </div>

          <div className="vc-imei-hub__stats-sidebar" aria-live="polite">
            {checksStatsReady ? (
              <ImeiHubStatsMinimal
                checksTotal={checksTotal}
                riskStats={riskStats}
                locale={i18n.language}
                t={t}
              />
            ) : (
              <p className="vc-imei-hub__stats-minimal vc-imei-hub__stats-minimal--loading" aria-hidden="true">
                <span className="vc-imei-hub__stat-row-skeleton vc-imei-hub__stat-row-skeleton--minimal" />
              </p>
            )}
          </div>
        </div>

        <div
          ref={hubCardsWrapRef}
          className={`vc-imei-hub__cards-wrap${hubCardsTileMode ? ' vc-imei-hub__cards-wrap--carousel' : ''}`}
        >
          {hubCardsTileMode ? (
            <div
              ref={hubCardsMarqueeRef}
              className="vc-imei-hub__cards-marquee"
              aria-label={t('vc.imeiHub.cardsLearnAria')}
            >
              <div ref={hubCardsMarqueeTrackRef} className="vc-imei-hub__cards-marquee-track">
                <div className="vc-imei-hub__cards vc-imei-hub__cards--mobile-tiles vc-imei-hub__cards--marquee-set">
                  {orderedServiceIds.map((id) => renderHubCard(id))}
                </div>
              </div>
            </div>
          ) : (
            <div
              className="vc-imei-hub__cards"
              role="radiogroup"
              aria-label={t('vc.imeiHub.cardsAria')}
            >
              {orderedServiceIds.map((id) => renderHubCard(id))}
            </div>
          )}

          <div
            id={panelId}
            className={`vc-imei-hub__panel${hubCardsTileMode ? ' vc-imei-hub__panel--mobile-docs' : ''}${expandedId ? ' vc-imei-hub__panel--open' : ''}`}
            aria-hidden={!expandedId}
          >
            <div className="vc-imei-hub__panel-inner">
              {expandedId && panelMountIds.has(expandedId) ? (
                <ImeiHubServicePanel
                  serviceId={expandedId}
                  priceEurCents={paidPriceCents[expandedId]}
                />
              ) : null}
            </div>
          </div>
        </div>

        {verifyFlowVisible ? (
          <VerifyFlow
            service={selected}
            serviceTitle={t(`vc.imeiHub.services.${selected}.title`)}
            imeiMasked={flowImeiMasked || checkedImeiMasked}
            sessionKey={verifySessionKey}
            loadingActive={verifyFlowLoadingActive}
            succeeded={verifySucceeded}
            failed={verifyFailed}
            errorMessage={verifyFlowError}
            onReveal={handleVerifyReveal}
            onRetry={() => runCheck()}
            onDismiss={verifyLoadingDismissible ? dismissVerifyLoadingModal : undefined}
            dismissible={verifyLoadingDismissible}
            revealReport={revealReport}
            savedToProfile={Boolean(token)}
          >
            {checkResult ? (
              <ImeiVerifyReport
                variant="inline"
                service={selected}
                data={checkResult}
                imeiMasked={checkedImeiMasked}
                checkedAt={checkedAt ?? undefined}
                token={token}
                imeiDigits={imei15}
                onRequireLogin={onRequireLogin}
                onNewCheck={() => {
                  clearOutput()
                  setValue('')
                }}
              />
            ) : null}
          </VerifyFlow>
        ) : null}

        {integrationNote ? (
          <p id={`${inputId}-note`} className="vc-imei-hub__integration" role="status">
            {integrationNote}
          </p>
        ) : null}

        <div className="vc-imei-hub__meta">
          <span className="vc-imei-hub__security">
            <ShieldIcon />
            {t('vc.imeiHub.security')}
          </span>
          <p className="vc-imei-hub__footer">
            {t('vc.imeiHub.privacy')} •{' '}
            {t('vc.imeiHub.selected', { service: t(`vc.imeiHub.services.${selected}.title`) })}
          </p>
        </div>
      </div>
    </section>
  )
}
